'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * @primitive DelayedSkeleton + useDelayedLoading (client)
 * ROLE: none — wrapper that gates a fallback (typically a Skeleton or
 *   SkeletonGroup) on a delay+min-duration timing window. Eliminates two
 *   classes of loading-flash:
 *     • Fast loads (<delay): skeleton never appears.
 *     • Slow-then-fast resolution: skeleton holds for ≥minDuration so it
 *       doesn't blink in for 30ms and disappear.
 * KEYBOARD: non-interactive
 * FOCUS: none
 * LIVE-REGION: none — wrap the fallback in SkeletonGroup for SR announcement.
 *   The delay window means screen readers won't be told "loading" on
 *   sub-200ms loads (which is the desired behavior — there's nothing to
 *   announce when the content arrives before the user can perceive it).
 * PRM: timing constants are fixed; PRM users still benefit from the
 *   anti-flash window (perception-of-motion is unrelated to motion-style).
 * RSC: CLIENT (timer-based state)
 *
 * Defaults follow industry practice (GitHub primer, Atlassian, Linear-style):
 *   delay = 200ms     — Nielsen's 100–200ms "instant" threshold; if content
 *                       resolves inside it, render nothing transient.
 *   minDuration = 300ms — Once the skeleton is visible, hold for at least
 *                         this long so the swap to content doesn't read as
 *                         a flicker.
 */
import { useEffect, useRef, useState } from 'react';
const DEFAULT_DELAY = 200;
const DEFAULT_MIN_DURATION = 300;
/**
 * Returns `true` only when a loading fallback should actually be rendered,
 * applying the delay (skip fallback on fast loads) and minDuration (hold
 * fallback so it doesn't flicker) rules described in the file header.
 *
 *   const showSkeleton = useDelayedLoading(query.isPending);
 *   return showSkeleton ? <FooSkeleton /> : <Foo data={query.data!} />;
 *
 * Prefer the `<DelayedSkeleton>` wrapper for the common case; reach for the
 * raw hook when the fallback/content shapes don't match a children-vs-fallback
 * split (multiple disjoint skeleton regions on the same page, etc.).
 */
export function useDelayedLoading(isLoading, { delay = DEFAULT_DELAY, minDuration = DEFAULT_MIN_DURATION } = {}) {
    const [shown, setShown] = useState(false);
    const shownAtRef = useRef(null);
    useEffect(() => {
        // Three meaningful states; the other two combinations are steady no-ops.
        //
        // 1. isLoading && !shown — schedule the appearance after `delay`. If
        //    isLoading flips back to false inside the delay, the cleanup cancels
        //    the timer and the skeleton never renders.
        if (isLoading && !shown) {
            const timer = setTimeout(() => {
                shownAtRef.current = Date.now();
                setShown(true);
            }, delay);
            return () => clearTimeout(timer);
        }
        // 2. !isLoading && shown — enforce minDuration before hiding so the
        //    skeleton doesn't blink in/out on a load that resolved just after
        //    the delay fired.
        if (!isLoading && shown) {
            const elapsed = Date.now() - (shownAtRef.current ?? Date.now());
            const remaining = Math.max(0, minDuration - elapsed);
            if (remaining === 0) {
                shownAtRef.current = null;
                setShown(false);
                return;
            }
            const timer = setTimeout(() => {
                shownAtRef.current = null;
                setShown(false);
            }, remaining);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isLoading, shown, delay, minDuration]);
    return shown;
}
/**
 * Declarative wrapper around `useDelayedLoading`. Renders nothing during the
 * pre-delay window, the fallback once delay elapses, and children once
 * loading clears (plus the min-duration hold).
 *
 *   <DelayedSkeleton
 *     isLoading={query.isPending}
 *     fallback={<MyCardSkeleton />}
 *   >
 *     {query.data ? <MyCard data={query.data} /> : null}
 *   </DelayedSkeleton>
 */
export function DelayedSkeleton({ isLoading, fallback, children, delay, minDuration, }) {
    const showFallback = useDelayedLoading(isLoading, delay !== undefined || minDuration !== undefined
        ? { ...(delay !== undefined ? { delay } : {}), ...(minDuration !== undefined ? { minDuration } : {}) }
        : undefined);
    if (showFallback)
        return _jsx(_Fragment, { children: fallback });
    if (isLoading)
        return null;
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=DelayedSkeleton.js.map